import { DayPicker, addToRange } from 'react-day-picker';

// import date-fns sub-modules
import { startOfDay, endOfDay, parse, format, subDays, subHours, addMinutes, addMilliseconds } from 'date-fns';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Text from "./Text.js";
import Flex from "./Flex.js";
import Selector from "./Selector.js";
import TextInput from "./TextInput.js";
import Button from "./Button.js";
import Popup from "./Popup.js";
import Theme from "./Theme.js";
import ToggleButton from "./ToggleButton.js";
import S from './DateTimeRangePicker.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = Theme.I18N.ns('DateTimeRangePicker');
const defaultRangeTypes = [{
  get text() {
    return t('DateTimeRangePicker.today', 'Today');
  },
  value: 'today',
  isMatch: ({
    start,
    end
  }) => {
    return start && end && start.getTime() === startOfDay(new Date()).getTime() && end.getTime() === endOfDay(new Date()).getTime();
  },
  getRange: () => {
    return {
      start: startOfDay(new Date()),
      end: endOfDay(new Date())
    };
  },
  renderer: {
    type: 'toggleButton',
    field: 'text'
  },
  getValue() {
    return this.value;
  }
}, {
  get text() {
    return t('DateTimeRangePicker.last24Hours', 'Last 24 Hours');
  },
  value: 'last24Hours',
  isMatch: ({
    start,
    end
  }) => {
    if (!start || !end) return false;
    const now = new Date();
    const twentyFourHoursAgo = subHours(now, 24);
    const startDiffHours = Math.abs((start.getTime() - twentyFourHoursAgo.getTime()) / (1000 * 60 * 60));
    const endDiffHours = Math.abs((end.getTime() - now.getTime()) / (1000 * 60 * 60));
    return startDiffHours <= 1 && endDiffHours <= 1;
  },
  getRange() {
    return {
      start: this.data.start(),
      end: this.data.end()
    };
  },
  renderer: {
    type: 'toggleButton',
    field: 'text'
  },
  data: {
    start: () => subHours(new Date(), 24),
    end: () => new Date()
  },
  getValue() {
    return this.value;
  }
}, {
  get text() {
    return t('DateTimeRangePicker.yesterday', 'Yesterday');
  },
  value: 'yesterday',
  isMatch: ({
    start,
    end
  }) => {
    return start && end && start.getTime() === startOfDay(subDays(new Date(), 1)).getTime() && end.getTime() === endOfDay(subDays(new Date(), 1)).getTime();
  },
  getRange: () => {
    return {
      start: startOfDay(subDays(new Date(), 1)),
      end: endOfDay(subDays(new Date(), 1))
    };
  },
  renderer: {
    type: 'toggleButton',
    field: 'text'
  },
  getValue() {
    return this.value;
  }
}, {
  get text() {
    return t('DateTimeRangePicker.last7Days', 'Last 7 Days');
  },
  value: 'last7Days',
  isMatch: ({
    start,
    end
  }) => {
    return start && end && start.getTime() === startOfDay(subDays(new Date(), 7)).getTime() && end.getTime() === endOfDay(new Date()).getTime();
  },
  getRange: () => {
    return {
      start: startOfDay(subDays(new Date(), 7)),
      end: endOfDay(new Date())
    };
  },
  renderer: {
    type: 'toggleButton',
    field: 'text'
  },
  getValue() {
    return this.value;
  }
}, {
  get text() {
    return t('DateTimeRangePicker.last30Days', 'Last 30 Days');
  },
  value: 'last30Days',
  isMatch: ({
    start,
    end
  }) => {
    return start && end && start.getTime() === startOfDay(subDays(new Date(), 30)).getTime() && end.getTime() === endOfDay(new Date()).getTime();
  },
  getRange: () => {
    return {
      start: startOfDay(subDays(new Date(), 30)),
      end: endOfDay(new Date())
    };
  },
  renderer: {
    type: 'toggleButton',
    field: 'text'
  },
  getValue() {
    return this.value;
  }
}, {
  get text() {
    return t('DateTimeRangePicker.custom', 'Custom');
  },
  value: 'custom',
  isMatch: (/* { start, end } */
  ) => {
    return true;
  },
  getRange: r => {
    if (r) {
      return {
        start: r.start,
        end: r.end
      };
    }
    return null;
  },
  getValue() {
    return this.value;
  },
  // custom is not a calculable value
  type: 'range'
}];
const generateTimeValues = timeFormat => {
  const values = [];
  const startOfToday = startOfDay(new Date());
  for (let i = 0; i < 48; i++) {
    const t = addMinutes(startOfToday, 30 * i);
    if (i !== 0) {
      const timeString = format(t, timeFormat);
      values.push({
        text: timeString,
        value: timeString,
        date: t
      });
    }
  }
  return values;
};
const DEFAULT_VALUES = [];
const DEFAULT_TIME_FORMAT = 'hh:mm a';
const startOfToday = startOfDay(new Date());
for (let i = 0; i < 47; i++) {
  const t = addMinutes(startOfToday, 30 * i);
  if (i !== 0) {
    DEFAULT_VALUES.push({
      text: format(t, DEFAULT_TIME_FORMAT),
      value: format(t, DEFAULT_TIME_FORMAT),
      date: t
    });
  }
}
const end = addMinutes(startOfToday, 24 * 60 - 1);
const defaultStartTimeValues = [{
  get text() {
    return t('DateTimeRangePicker.startOfDay', 'start of day');
  },
  value: '12:00 AM',
  date: startOfToday,
  transform: t => startOfDay(t)
}, ...DEFAULT_VALUES];
const defaultEndTimeValues = [{
  get text() {
    return t('DateTimeRangePicker.endOfDay', 'end of day');
  },
  value: '11:59 PM',
  date: end,
  transform: t => endOfDay(t)
}, ...DEFAULT_VALUES];
function validateHhMm(time) {
  // const isValid = /((1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm]))/.test( time );
  // return isValid;
  const timeBasicFormat = time.substring(0, 8);
  const basicFormat = /^(0?[1-9]|1[0-2]):([0-5][0-9])(\s?)([AaPp][Mm])$/.test(timeBasicFormat);
  const timeSecondsFormat = time.substring(0, 11);
  const withSeconds = /^(0?[1-9]|1[0-2]):([0-5][0-9]):([0-5][0-9])(\s?)([AaPp][Mm])$/.test(timeSecondsFormat);
  return basicFormat || withSeconds;
}

/**
 * React class that renders a date range picker 
 * @property {string} className css class to be applied to this component
 * @property {string} variant Theme variant name to be added to the component
 * @property {function} onClick function to be called when the button is clicked
 * @property {any} eventData will be passed through as the data key to the onClick function
 * @property {node} internal component children, will be handled by react
 * @property {string} font string corresponding to the icon to be rendered
 * */
class DateTimeRangePicker extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    open: PropTypes.bool,
    closeOnApply: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    })]),
    editValue: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    })]),
    stsVal: PropTypes.string,
    etsVal: PropTypes.string,
    // end editing props
    onChange: PropTypes.func.isRequired,
    onUiStateChange: PropTypes.func,
    rangeTypes: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
      isMatch: PropTypes.func,
      getRange: PropTypes.func,
      data: PropTypes.any
    })),
    dateFormat: PropTypes.string,
    timeFormat: PropTypes.string,
    displayTime: PropTypes.bool,
    startTimeValues: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string
    })),
    endTimeValues: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string
    })),
    dayPickerProps: PropTypes.object,
    removeTimeText: PropTypes.string,
    addTimeText: PropTypes.string,
    // ui props
    popupContainerClassName: PropTypes.string,
    dateTimeEditorClassName: PropTypes.string,
    className: PropTypes.string,
    cancelIcon: PropTypes.string,
    applyIcon: PropTypes.string,
    startDateLabelClassName: PropTypes.string,
    startTimeLabelClassName: PropTypes.string,
    arrowIcon: PropTypes.string,
    endDateLabelClassName: PropTypes.string,
    endTimeLabelClassName: PropTypes.string,
    endTimeInputProps: PropTypes.string,
    startTimeInputProps: PropTypes.string,
    variant: PropTypes.string,
    cancelVariant: PropTypes.string,
    applyVariant: PropTypes.string,
    toggleTimeOfDayVariant: PropTypes.string,
    toggleLeftIcon: PropTypes.string,
    toggleRightIcon: PropTypes.string,
    popupContainerVariant: PropTypes.string,
    textVariant: PropTypes.string,
    comboboxVariant: PropTypes.string,
    inputVariant: PropTypes.string,
    toggleVariant: PropTypes.string,
    validator: PropTypes.func,
    rangeChangeType: PropTypes.string,
    startHint: PropTypes.string,
    endHint: PropTypes.string
  };
  static defaultProps = {
    onUiStateChange: () => {},
    open: false,
    closeOnApply: true,
    dayPickerProps: null,
    value: {},
    editValue: {},
    stsVal: null,
    etsVal: null,
    rangeTypes: defaultRangeTypes,
    // timeFormat : 'hh:mm a',
    // dateFormat : 'dd-MM-yy',
    endTimeValues: defaultEndTimeValues,
    startTimeValues: defaultStartTimeValues,
    displayTime: false,
    removeTimeText: 'Remove Time',
    addTimeText: 'Add Time',
    // ui props
    variant: null,
    popupContainerClassName: null,
    dateTimeEditorClassName: null,
    className: null,
    cancelVariant: 'dateRangePickerCancel',
    applyVariant: 'dateRangePickerApply',
    toggleTimeOfDayVariant: 'dateRangePickerToggleTimeOfDay',
    popupContainerVariant: 'dateRangePickerPopupContainer',
    cancelIcon: null,
    applyIcon: null,
    toggleLeftIcon: null,
    toggleRightIcon: null,
    startDateLabelClassName: null,
    startTimeLabelClassName: null,
    arrowIcon: 'fa fa-caret-right',
    endDateLabelClassName: null,
    endTimeLabelClassName: null,
    endTimeInputProps: null,
    startTimeInputProps: null,
    rangeChangeType: 'alternate',
    startHint: 'Start Date',
    endHint: 'End Date'
  };
  constructor(props, context) {
    super(props, context);
    const {
      start,
      end
    } = this.getRange();
    this.startDateInput = /*#__PURE__*/React.createRef();
    this.endDateInput = /*#__PURE__*/React.createRef();
    this.state = {
      displayTime: props.displayTime || this.hasTime(),
      startTimeOpen: false,
      endTimeOpen: false,
      changeStart: true,
      startDateError: false,
      endDateError: false,
      selectedRange: {
        from: start ? startOfDay(start) : null,
        to: end ? endOfDay(end) : null
      }
    };
  }
  forceInputUpdate = () => {
    if (this.startDateInput.current) {
      this.startDateInput.current.setState({
        value: this.startDateInput.current.props.value
      });
    }
    if (this.endDateInput.current) {
      this.endDateInput.current.setState({
        value: this.endDateInput.current.props.value
      });
    }
  };
  getRange() {
    const {
      rangeTypes,
      value
    } = this.props;
    if (typeof value === 'string') {
      const range = rangeTypes.find(r => r.value === value);
      return range && range.getRange() || {};
    }
    return value || {};
  }
  getEditRange() {
    const {
      rangeTypes,
      editValue
    } = this.props;
    if (typeof editValue === 'string') {
      const range = rangeTypes.find(r => r.value === editValue);
      return range && range.getRange() || {};
    }
    return editValue || {};
  }
  getValue() {
    const {
      rangeTypes,
      value
    } = this.props;
    if (typeof value !== 'string') {
      const range = rangeTypes.find(r => r.isMatch(this.getRange()));
      return range && range.getValue(this.getRange());
    }
    return value;
  }
  getEditValue() {
    const {
      rangeTypes,
      editValue
    } = this.props;
    if (typeof editValue !== 'string') {
      const range = rangeTypes.find(r => r.isMatch(this.getEditRange()));
      return range && range.getValue(this.getEditRange());
    }
    return editValue;
  }
  setEditValue = (newRange, rest) => {
    const {
      rangeTypes,
      onUiStateChange
    } = this.props;
    const old = this.getEditRange();
    let editValue;
    if (typeof newRange === 'string') {
      const range = rangeTypes.find(r => r.value === newRange);
      if (range && range.type === 'range') {
        editValue = range.getRange(old);
      } else {
        editValue = newRange;
      }
    } else {
      editValue = newRange;
    }
    if (editValue?.start || editValue?.end) {
      this.setState({
        selectedRange: {
          from: editValue.start ? startOfDay(editValue.start) : null,
          to: editValue.end ? endOfDay(editValue.end) : null
        }
      });
    }
    onUiStateChange({
      type: 'editValue',
      value: {
        editValue,
        ...rest
      }
    });
  };
  getFormats() {
    const theme = this.context;
    let {
      timeFormat,
      dateFormat
    } = theme.props('DateTimeRangePicker', this.props);
    try {
      dateFormat = typeof dateFormat === 'string' ? dateFormat : 'dd-MM-yy';
      format(new Date(), dateFormat);
    } catch (x) {
      // console.error( x )
      dateFormat = 'dd-MM-yy';
    }
    try {
      timeFormat = typeof timeFormat === 'string' ? timeFormat : 'hh:mm a';
      format(Date.now(), timeFormat);
    } catch (x) {
      // console.error( x )
      timeFormat = 'hh:mm a';
    }
    const timeValues = generateTimeValues(timeFormat);
    const startTimeValues = [{
      get text() {
        return t('DateTimeRangePicker.startOfDay', 'start of day');
      },
      value: format(startOfDay(new Date()), timeFormat),
      date: startOfDay(new Date()),
      transform: t => startOfDay(t)
    }, ...timeValues];
    const endTimeValues = [{
      get text() {
        return t('DateTimeRangePicker.endOfDay', 'end of day');
      },
      value: format(endOfDay(new Date()), timeFormat),
      date: endOfDay(new Date()),
      transform: t => endOfDay(t)
    }, ...timeValues];
    return {
      timeFormat,
      dateFormat,
      startTimeValues,
      endTimeValues
    };
  }
  setValue = (newRange, rest) => {
    const {
      rangeTypes,
      onChange,
      onUiStateChange,
      eventData
    } = this.props;
    const old = this.getRange();
    let value;
    if (typeof newRange === 'string') {
      const range = rangeTypes.find(r => r.value === newRange);
      if (range && range.type === 'range') {
        value = range.getRange(old);
      } else {
        value = newRange;
      }
    } else {
      value = newRange;
    }
    onChange({
      type: 'value',
      data: eventData,
      value
    });
    onUiStateChange({
      type: 'value',
      value: rest
    });
  };
  setRangeType = ({
    value
  }) => {
    this.setEditValue(value);
  };
  onDateChange = (oldDate, newDate, timeString, stateField) => {
    const {
      timeFormat,
      dateFormat
    } = this.getFormats();
    const toSet = parse(`${newDate} ${oldDate && format(oldDate, timeFormat) || timeString}`, `${dateFormat} ${timeFormat}`, new Date());
    // Only check date to make sure its good
    // const toSet = parse( newDate, `${dateFormat}`, new Date() )
    const isValid = toSet.toString() !== 'Invalid Date';
    this.setState({
      [stateField === 'start' ? 'startDateError' : 'endDateError']: !isValid
    });
    if (isValid) {
      this.setEditValue({
        ...this.getEditRange(),
        [stateField]: toSet
      });
    }
  };
  onEndDateChange = ({
    value: end
  }) => {
    const {
      end: oldEndDate
    } = this.getEditRange();
    this.onDateChange(oldEndDate, end, this.getEtsVal(), 'end');
  };
  onStartDateChange = ({
    value: start
  }) => {
    const {
      start: oldStartDate
    } = this.getEditRange();
    this.onDateChange(oldStartDate, start, this.getStsVal(), 'start');
  };
  onStartDateBlur = evt => {
    const {
      start: oldStartDate
    } = this.getEditRange();
    const start = evt.target.value;
    this.onDateChange(oldStartDate, start, this.getStsVal(), 'start');
  };
  onEndDateBlur = evt => {
    const {
      end: oldEndDate
    } = this.getEditRange();
    const end = evt.target.value;
    this.onDateChange(oldEndDate, end, this.getEtsVal(), 'end');
  };
  onStartDateKeyDown = evt => {
    if (evt.key === 'Enter') {
      const start = evt.target.value;
      const {
        start: oldStartDate
      } = this.getEditRange();
      this.onDateChange(oldStartDate, start, this.getStsVal(), 'start');
    }
  };
  onEndDateKeyDown = evt => {
    if (evt.key === 'Enter') {
      const end = evt.target.value;
      const {
        end: oldEndDate
      } = this.getEditRange();
      this.onDateChange(oldEndDate, end, this.getEtsVal(), 'end');
    }
  };
  onTimeChange = (evt, oldDate, values, stringField, dateField) => {
    const {
      onUiStateChange
    } = this.props;
    const {
      dateFormat,
      timeFormat
    } = this.getFormats();
    if (typeof evt.value === 'string') {
      onUiStateChange({
        type: 'timeChange',
        value: {
          [stringField]: evt.value
        }
      });
      if (evt?.value?.length > 7 && validateHhMm(evt?.value)) {
        // const newDate = parse( `${oldDate && format( oldDate, dateFormat )} ${evt?.value}`, `${dateFormat} ${timeFormat}`, new Date() )
        // this.setEditValue( { ...this.getEditRange(), [dateField] : newDate }, { [stringField] : format( newDate, timeFormat ) } )
        const dateToFormat = oldDate || new Date();
        const newDate = parse(`${format(dateToFormat, dateFormat)} ${evt.value}`, `${dateFormat} ${timeFormat}`, new Date());
        this.setEditValue({
          ...this.getEditRange(),
          [dateField]: newDate
        }, {
          [stringField]: format(newDate, timeFormat)
        });
      }
    } else if (evt.value != null) {
      const {
        value,
        text,
        transform,
        format: evtFormat
      } = evt.value;
      const timeToParse = format(parse(value, evtFormat || DEFAULT_TIME_FORMAT, new Date()), timeFormat);
      let newDate = parse(`${oldDate && format(oldDate, dateFormat)} ${timeToParse}`, `${dateFormat} ${timeFormat}`, new Date());
      if (transform) {
        newDate = transform(newDate);
      }
      if (newDate && newDate.toString() !== 'Invalid Date') {
        this.setEditValue({
          ...this.getEditRange(),
          [dateField]: newDate
        }, {
          [stringField]: format(newDate, timeFormat)
        });
      } else {
        onUiStateChange({
          type: 'timeChange',
          value: {
            [stringField]: text || this.getMatchingSuggestion(values, value) || value
          }
        });
      }
    }
  };
  onStartTimeChange = evt => {
    const {
      startTimeValues
    } = this.props;
    const {
      start
    } = this.getEditRange();
    this.onTimeChange(evt, start, startTimeValues, 'stsVal', 'start');
  };
  onEndTimeChange = evt => {
    const {
      endTimeValues
    } = this.props;
    const {
      end
    } = this.getEditRange();
    this.onTimeChange(evt, end, endTimeValues, 'etsVal', 'end');
  };
  toggleTimeOfDay = () => {
    // eslint-disable-next-line prefer-const
    let {
      displayTime,
      onUiStateChange,
      startTimeValues,
      endTimeValues,
      dateFormat
    } = this.props;
    const {
      timeFormat
    } = this.getFormats();
    // if ( this.hasTime() ) {
    //   displayTime = true
    //   const stsVal = format( startTimeValues[0].date, timeFormat )
    //   const etsVal = format( endTimeValues[0].date, timeFormat )
    //   // SMJ
    //   const { start, end } = this.getEditRange()
    //   const sd = start == null ? new Date() : start //parse( start, `${dateFormat} ${timeFormat}` )
    //   const ed = end == null ? new Date() : end //parse( end, `${dateFormat} ${timeFormat}` )
    //   const sf = format( sd, dateFormat ) + ' ' + stsVal
    //   const ef = format( ed, dateFormat ) + ' ' + etsVal
    //   this.setEditValue( { start : sf, end : ef } )
    // }
    if (displayTime) {
      const {
        start,
        end
      } = this.getEditRange();
      const newStart = start ? startOfDay(start) : null;
      const newEnd = end ? endOfDay(end) : null;
      this.setEditValue({
        start: newStart,
        end: newEnd
      }, {
        stsVal: newStart ? format(newStart, timeFormat) : null,
        etsVal: newEnd ? format(newEnd, timeFormat) : null,
        displayTime: false
      });
    }
    onUiStateChange({
      type: !displayTime ? 'showTime' : 'hideTime',
      value: {
        displayTime: !displayTime
      }
    });
  };
  setOpen = open => {
    const {
      onUiStateChange
    } = this.props;
    onUiStateChange({
      type: open ? 'open' : 'close',
      value: {
        open
      }
    });
  };
  open = () => {
    this.setOpen(true);
  };
  close = () => {
    this.setOpen(false);
  };
  onToggleStartTime = evt => {
    this.setState({
      startTimeOpen: evt
    });
  };
  onToggleEndTime = evt => {
    this.setState({
      endTimeOpen: evt
    });
  };
  startTextChange = ({
    value,
    isReturn
  }) => {
    const {
      closeOnApply,
      open
    } = this.props;
    const {
      dateFormat,
      timeFormat
    } = this.getFormats();
    let newStart = parse(value, `${dateFormat} ${timeFormat}`, new Date());
    if (newStart.toString() === 'Invalid Date') {
      this.setState({
        startDateError: true
      });
      newStart = parse(`${value} ${this.getStsVal()}`, `${dateFormat} ${timeFormat}`, new Date());
    }
    if (newStart.toString() !== 'Invalid Date') {
      this.setState({
        startDateError: false
      });
      this.setValue({
        ...this.getRange(),
        start: newStart
      }, {
        open: closeOnApply ? false : open
      });
      this.setEditValue({
        ...this.getEditRange(),
        start: newStart
      }, {
        stsVal: format(newStart, timeFormat)
      });
    }
  };
  endTextChange = ({
    value,
    isReturn
  }) => {
    const {
      closeOnApply,
      open
    } = this.props;
    const {
      dateFormat,
      timeFormat
    } = this.getFormats();
    let newEnd = parse(value, `${dateFormat} ${timeFormat}`, new Date());
    if (newEnd.toString() === 'Invalid Date') {
      this.setState({
        endDateError: true
      });
      newEnd = parse(`${value} ${this.getEtsVal()}`, `${dateFormat} ${timeFormat}`, new Date());
    }
    if (newEnd.toString() !== 'Invalid Date') {
      this.setState({
        endDateError: false
      });
      this.setValue({
        ...this.getRange(),
        end: newEnd
      }, {
        open: closeOnApply ? false : open
      });
      this.setEditValue({
        ...this.getEditRange(),
        end: newEnd
      }, {
        etsVal: format(newEnd, timeFormat)
      });
    }
  };
  inputTrigger = ({
    ref,
    onToggle,
    isOpened
  }) => {
    const {
      arrowIcon,
      variant,
      className,
      rangeTypes,
      toggleVariant,
      inputVariant,
      toggleLeftIcon,
      toggleRightIcon,
      startHint,
      endHint
    } = this.props;
    const {
      dateFormat,
      timeFormat
    } = this.getFormats();
    const {
      start,
      end
    } = this.getRange();
    const {
      startDateError,
      endDateError
    } = this.state;
    const theme = this.context;
    const range = rangeTypes.find(r => r.value === this.getValue());
    if (range && range.renderer) {
      if (range.renderer.type === 'toggleButton') {
        return /*#__PURE__*/_jsxs(ToggleButton, {
          className: classnames(classnames(theme.classes('DateTimeRangePicker', 'dateTimeRangePicker', variant, className), S.dateTimeRangePicker), S.toggleButtonTrigger, 'toggleButtonTrigger', 'trigger'),
          variant: toggleVariant,
          value: isOpened,
          domRef: ref,
          onChange: onToggle,
          children: [toggleLeftIcon ? /*#__PURE__*/_jsx(Text, {
            variant: `${toggleVariant}LeftIcon`,
            children: /*#__PURE__*/_jsx("i", {
              className: toggleLeftIcon
            })
          }) : '', range[range.renderer.field], toggleRightIcon ? /*#__PURE__*/_jsx(Text, {
            variant: `${toggleVariant}RightIcon`,
            children: /*#__PURE__*/_jsx("i", {
              className: toggleRightIcon
            })
          }) : '']
        });
      }
      if (range.renderer.type === 'custom') {
        return range.renderer.render({
          ...this.props,
          ref,
          onToggle,
          isOpened
        }, range);
      }
    }
    return /*#__PURE__*/_jsxs("div", {
      className: classnames(theme.classes('DateTimeRangePicker', 'dateTimeRangePicker', variant, className), S.dateTimeRangePicker, 'trigger'),
      ref: ref,
      onClick: !isOpened ? this.open : null,
      children: [/*#__PURE__*/_jsx(TextInput, {
        changeOnBlur: true,
        hint: t('startHint', startHint),
        variant: `${inputVariant}Left|${inputVariant}`,
        size: dateFormat.length + timeFormat.length + 4,
        onChange: this.startTextChange,
        className: classnames(S.startDate, 'startDate', {
          [S.error]: startDateError
        }),
        value: start && format(start, `${dateFormat} ${timeFormat}`)
      }), /*#__PURE__*/_jsx(Flex, {
        align: "center",
        className: classnames(S.arrow, 'arrow'),
        children: /*#__PURE__*/_jsx("i", {
          className: arrowIcon
        })
      }), /*#__PURE__*/_jsx(TextInput, {
        changeOnBlur: true,
        hint: t('endHint', endHint),
        variant: `${inputVariant}Right|${inputVariant}`,
        size: dateFormat.length + timeFormat.length + 4,
        onChange: this.endTextChange,
        className: classnames(S.endDate, 'endDate', {
          [S.error]: endDateError
        }),
        value: end && format(end, `${dateFormat} ${timeFormat}`)
      })]
    });
  };
  addDayToRange = (newDate, {
    from: nStart,
    to: nEnd
  }, type) => {
    const {
      changeStart
    } = this.state;

    // at least one bound is unset
    if (!nStart && !nEnd) {
      return {
        from: newDate,
        to: nEnd
      };
    }

    // only end date is set
    if (!nStart && nEnd) {
      if (newDate < nEnd) {
        return {
          from: newDate,
          to: nEnd
        };
      }
      return {
        from: nEnd,
        to: newDate
      };
    }

    // only start date is set
    if (nStart && !nEnd) {
      if (newDate < nStart) {
        return {
          from: newDate,
          to: null
        };
      }
      return {
        from: nStart,
        to: newDate
      };
    }
    if (newDate < nStart) {
      return {
        from: newDate,
        to: nEnd
      };
    }
    // clicking on start date changes end date
    if (newDate.getDate() === nStart.getDate() && newDate.getMonth() === nStart.getMonth() && newDate.getFullYear() === nStart.getFullYear()) {
      return {
        from: nStart,
        to: newDate
      };
    }
    if (nStart < newDate && newDate < nEnd) {
      // always change start bound
      if (type === 'start') {
        return {
          from: newDate,
          to: nEnd
        };
      }
      let res;
      if (changeStart) {
        res = {
          from: newDate,
          to: nEnd
        };
      } else {
        res = {
          from: nStart,
          to: newDate
        };
      }
      this.setState({
        changeStart: !changeStart
      });
      return res;
    }
    return {
      from: nEnd,
      to: newDate
    };
  };
  onDayClick = newDate => {
    const {
      timeFormat
    } = this.getFormats();
    const {
      etsVal,
      stsVal
    } = this.props;
    const theme = this.context;
    const {
      rangeChangeType
    } = theme.props('DateTimeRangePicker', this.props);
    const {
      startDateError,
      endDateError
    } = this.state;
    if (!newDate || newDate.toString() === 'Invalid Date') {
      console.warn('Invalid date provided to onDayClick:', newDate);
      return;
    }
    const {
      start: nStart,
      end: nEnd
    } = this.getEditRange();
    let range;
    if (rangeChangeType === 'end') {
      const validRange = {
        from: nStart || newDate,
        to: nEnd || null
      };
      range = addToRange(newDate, validRange);
    } else {
      range = this.addDayToRange(newDate, {
        from: nStart,
        to: nEnd
      }, rangeChangeType);
    }
    const from = range.from;
    const to = range.to;
    if (startDateError || endDateError) {
      this.setState({
        ...(startDateError && {
          startDateError: false
        }),
        ...(endDateError && {
          endDateError: false
        })
      });
    }
    let startDiff = 0;
    if (nStart) {
      startDiff = nStart.getTime() - startOfDay(nStart).getTime();
    } else if (stsVal) {
      const val = parse(stsVal, timeFormat, new Date());
      startDiff = val.getTime() - startOfDay(val).getTime();
    }
    let endDiff = 0;
    if (nEnd) {
      endDiff = nEnd.getTime() - startOfDay(nEnd).getTime();
    } else if (etsVal) {
      const val = parse(etsVal, timeFormat, new Date());
      endDiff = val.getTime() - startOfDay(val).getTime();
    }
    const newStart = from && addMilliseconds(startOfDay(from), startDiff);
    const newEnd = to && addMilliseconds(startOfDay(to), endDiff);
    this.setState({
      displayTime: false,
      startTimeOpen: false,
      endTimeOpen: false
    });
    this.setEditValue({
      start: newStart,
      end: newEnd
    }, {
      stsVal: null,
      etsVal: null,
      displayTime: false
    });
    this.props.onUiStateChange({
      type: 'hideTime',
      value: {
        displayTime: false
      }
    });
    this.forceInputUpdate();
  };
  onCancel = () => {
    const {
      timeFormat
    } = this.getFormats();
    const value = this.getValue();
    const {
      start,
      end
    } = this.getRange();
    this.setEditValue(this.getRange(), {
      editValue: this.getValue(),
      stsVal: start && format(start, timeFormat),
      etsVal: end && format(end, timeFormat),
      open: false
    });
  };
  onApply = () => {
    const {
      editValue,
      open,
      closeOnApply
    } = this.props;
    this.setValue(editValue, {
      open: closeOnApply ? false : open
    });
  };
  startTimeInputBlur = () => {
    this.selectOnEnter = false;
    const {
      stsVal,
      nStart
    } = this.props;
    if (stsVal && nStart) {
      this.onStartTimeChange({
        value: stsVal
      });
    }
  };
  endTimeInputBlur = () => {
    this.selectOnEnter = false;
    const {
      etsVal,
      nEnd
    } = this.props;
    if (etsVal && nEnd) {
      this.onEndTimeChange({
        value: etsVal
      });
    }
  };
  listenForStartEnter = evt => {
    const {
      selectOnEnter
    } = this;
    if (evt.key === 'Enter' && !selectOnEnter) {
      evt.preventDefault();
      const {
        stsVal
      } = this.props;
      this.onStartTimeChange({
        value: stsVal
      });
      this.setState({
        startTimeOpen: false
      });
    }
    if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
      this.selectOnEnter = true;
    } else {
      this.selectOnEnter = false;
    }
  };
  listenForEndEnter = evt => {
    const {
      selectOnEnter
    } = this;
    if (evt.key === 'Enter' && !selectOnEnter) {
      evt.preventDefault();
      const {
        etsVal
      } = this.props;
      this.onEndTimeChange({
        value: etsVal
      });
      this.setState({
        endTimeOpen: false
      });
    }
    if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
      this.selectOnEnter = true;
    } else {
      this.selectOnEnter = false;
    }
  };
  handlePopup = evt => {
    if (evt && typeof evt.isOpened) {
      this.setOpen(evt.isOpened);
    }
  };
  getMatchingSuggestion(suggestions, text) {
    return suggestions.find(s => s.value === text);
  }
  getStsVal() {
    const {
      start: nStart
    } = this.getEditRange();
    const {
      startTimeValues
    } = this.props;
    const {
      timeFormat
    } = this.getFormats();
    let {
      stsVal
    } = this.props;
    if (stsVal != null) {
      return stsVal;
    }
    if (nStart) {
      const m = this.getMatchingSuggestion(startTimeValues, format(nStart, timeFormat));
      stsVal = m != null ? format(m.date, timeFormat) : format(nStart, timeFormat);
    }
    if (stsVal == null) {
      stsVal = format(startTimeValues[0].date, timeFormat);
    }
    return stsVal;
  }
  getEtsVal() {
    const {
      end: nEnd
    } = this.getEditRange();
    const {
      endTimeValues
    } = this.props;
    const {
      timeFormat
    } = this.getFormats();
    let {
      etsVal
    } = this.props;
    if (etsVal != null) {
      return etsVal;
    }
    if (nEnd) {
      const m = this.getMatchingSuggestion(endTimeValues, format(nEnd, timeFormat));
      etsVal = m != null ? format(m.date, timeFormat) : format(nEnd, timeFormat);
    }
    if (etsVal == null) {
      etsVal = format(endTimeValues[0].date, timeFormat);
    }
    return etsVal;
  }
  hasTime() {
    const stsVal = this.getStsVal();
    const etsVal = this.getEtsVal();
    const {
      startTimeValues,
      endTimeValues
    } = this.props;
    const isStartOfDay = this.getMatchingSuggestion(startTimeValues, stsVal) === startTimeValues[0];
    const isEndOfDay = this.getMatchingSuggestion(endTimeValues, etsVal) === endTimeValues[0];
    return !isStartOfDay || !isEndOfDay;
  }
  renderDay = (day, {
    start,
    end
  }) => {
    return /*#__PURE__*/_jsx("div", {
      className: S.circle,
      children: day.getDate()
    });
  };
  validateTime = () => {
    const {
      validator
    } = this.props;
    const {
      start,
      end
    } = this.getEditRange();
    const {
      startDateError,
      endDateError
    } = this.state;
    if (startDateError || endDateError) {
      return true;
    }
    if (typeof validator === 'function') {
      return validator({
        start,
        end
      }) === false;
    }
    return !(!!start && !!end && start < end);
  };
  render() {
    const theme = this.context;
    const {
      dateFormat,
      timeFormat
    } = this.getFormats();
    const {
      rangeTypes,
      dayPickerProps,
      removeTimeText,
      addTimeText,
      open,
      startTimeValues,
      endTimeValues,
      className,
      popupContainerClassName,
      dateTimeEditorClassName,
      startDateLabelClassName,
      startTimeLabelClassName,
      arrowIcon,
      endDateLabelClassName,
      endTimeLabelClassName,
      cancelIcon,
      applyIcon,
      variant,
      cancelVariant,
      applyVariant,
      toggleTimeOfDayVariant,
      textVariant,
      comboboxVariant,
      inputVariant,
      startHint,
      endHint
    } = this.props;
    const {
      displayTime
    } = this.props;
    const {
      start: nStart,
      end: nEnd
    } = this.getEditRange();
    const nSelectedRange = this.getEditValue();
    const stsVal = this.getStsVal();
    const etsVal = this.getEtsVal();
    const {
      startDateError,
      endDateError
    } = this.state;
    const start = nStart;
    const end = nEnd;
    const modifiers = {
      'DayPicker-Day--selected': day => {
        if (!start || !end) return false;
        const date = startOfDay(day);
        return date >= startOfDay(start) && date <= startOfDay(end);
      },
      'DayPicker-Day--start': day => start && startOfDay(day).getTime() === startOfDay(start).getTime(),
      'DayPicker-Day--end': day => end && startOfDay(day).getTime() === startOfDay(end).getTime()
    };
    const {
      t
    } = Theme.I18N.ns('DateTimeRangePicker');
    return /*#__PURE__*/_jsx(Popup, {
      onChange: this.handlePopup,
      opened: open,
      className: classnames(theme.classes('DateTimeRangePicker', 'dateTimeRangePicker', variant, className), S.popup, 'popup'),
      trigger: this.inputTrigger,
      attachment: "top left",
      targetAttachment: "bottom left",
      children: /*#__PURE__*/_jsxs(Flex, {
        className: popupContainerClassName,
        direction: "column",
        children: [/*#__PURE__*/_jsxs(Flex, {
          justify: "space-between",
          align: "center",
          className: S.predeterminedTz,
          children: [/*#__PURE__*/_jsx(Selector, {
            variant: "dateTimeRangeSelector|selector",
            toggleVariant: "dateTimeRangeSelector|selector",
            icon: tIcon('rangeDropdown', 'fa fa-fw fa-chevron-down'),
            valueKey: "value",
            nameKey: "text",
            onChange: this.setRangeType,
            value: nSelectedRange,
            className: classnames(S.rangeType, 'rangeType'),
            values: rangeTypes
          }), /*#__PURE__*/_jsxs(Text, {
            variant: `${textVariant}Timezone|${textVariant}`,
            className: "timezone",
            children: [/*#__PURE__*/_jsx("i", {
              className: classnames(S.globe, 'fa fa-globe')
            }), Intl && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions && Intl.DateTimeFormat().resolvedOptions().timeZone]
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          align: "flex-end",
          className: classnames(dateTimeEditorClassName, S.dateTimeEditor),
          children: [/*#__PURE__*/_jsxs(Flex, {
            direction: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: `${textVariant}Start|${textVariant}`,
              className: classnames(startDateLabelClassName, S.inputLabel),
              children: t('startDate', 'START DATE')
            }), /*#__PURE__*/_jsx(TextInput, {
              ref: this.startDateInput,
              changeOnBlur: true,
              onBlur: this.onStartDateBlur,
              onKeyDown: this.onStartDateKeyDown,
              hint: t('startHint', startHint),
              variant: `${inputVariant}PopupLeft|${inputVariant}Popup|${inputVariant}Left|${inputVariant}`,
              className: classnames(S.startDate, 'startDate', {
                [S.error]: startDateError
              }),
              onChange: this.onStartDateChange,
              value: nStart && format(nStart, dateFormat)
            })]
          }), displayTime ? /*#__PURE__*/_jsxs(Flex, {
            className: S.timeEditor,
            direction: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: `${textVariant}Time|${textVariant}`,
              className: classnames(startTimeLabelClassName, S.inputLabel),
              children: t('time', 'TIME')
            }), /*#__PURE__*/_jsx(Selector, {
              variant: comboboxVariant,
              values: this.getFormats().startTimeValues,
              nameKey: "text",
              valueKey: "value",
              onChange: this.onStartTimeChange,
              value: stsVal
            })]
          }) : null, /*#__PURE__*/_jsx(Flex, {
            align: "center",
            className: S.arrow,
            children: /*#__PURE__*/_jsx("i", {
              className: arrowIcon
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            direction: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: `${textVariant}End|${textVariant}`,
              className: classnames(endDateLabelClassName, S.inputLabel),
              children: t('endDate', 'END DATE')
            }), /*#__PURE__*/_jsx(TextInput, {
              ref: this.endDateInput,
              changeOnBlur: true,
              onBlur: this.onEndDateBlur,
              onKeyDown: this.onEndDateKeyDown,
              hint: t('endHint', endHint),
              variant: `${inputVariant}PopupRight|${inputVariant}Popup|${inputVariant}Right|${inputVariant}`,
              className: classnames(S.endDate, 'endDate', {
                [S.error]: endDateError
              }),
              onChange: this.onEndDateChange,
              value: nEnd && format(nEnd, dateFormat)
            })]
          }), displayTime ? /*#__PURE__*/_jsxs(Flex, {
            className: S.timeEditor,
            direction: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: `${textVariant}Time|${textVariant}`,
              className: classnames(endTimeLabelClassName, S.inputLabel),
              children: t('time', 'TIME')
            }), /*#__PURE__*/_jsx(Selector, {
              variant: comboboxVariant,
              values: this.getFormats().endTimeValues,
              nameKey: "text",
              valueKey: "value",
              onChange: this.onEndTimeChange,
              value: etsVal
            })]
          }) : null, /*#__PURE__*/_jsx(Button, {
            className: classnames('timeToggle', S.timeToggle),
            variant: `${toggleTimeOfDayVariant}|link`,
            onClick: this.toggleTimeOfDay,
            children: displayTime ? t('removeTimeText', removeTimeText) : t('addTimeText', addTimeText)
          })]
        }), /*#__PURE__*/_jsx(Flex, {
          className: S.calendar,
          children: /*#__PURE__*/_jsx(DayPicker, {
            mode: "range",
            className: S.selectable,
            selected: this.state.selectedRange,
            onSelect: range => {
              if (range) {
                this.setState({
                  selectedDays: range
                }, () => {
                  const newStart = range.from && startOfDay(range.from);
                  const newEnd = range.to && endOfDay(range.to);
                  this.setEditValue({
                    start: newStart,
                    end: newEnd
                  });
                });
              }
            },
            numberOfMonths: 2,
            modifiers: modifiers,
            onDayClick: this.onDayClick,
            renderDay: this.renderDay,
            ...dayPickerProps
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          className: S.controls,
          justify: "flex-end",
          children: [/*#__PURE__*/_jsx(Button, {
            icon: cancelIcon,
            className: classnames('cancel', S.cancel),
            onClick: this.onCancel,
            variant: `${cancelVariant}|secondary`,
            children: t('cancel', 'Cancel')
          }), /*#__PURE__*/_jsx(Button, {
            disabled: this.validateTime(),
            icon: applyIcon,
            className: classnames('apply', S.apply),
            onClick: this.onApply,
            variant: `${applyVariant}|primary`,
            children: t('submit', 'Apply')
          })]
        })]
      })
    });
  }
}
class Uncontrolled extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {};
    if (props.value) {
      this.state.editValue = props.value;
    }
  }
  onChange = evt => {
    const {
      onChange
    } = this.props;
    this.setState({
      value: evt.value
    });
    if (onChange) {
      onChange(evt);
    }
  };
  onUiStateChange = evt => {
    const {
      onUiStateChange
    } = this.props;
    this.setState(evt.value);
    if (onUiStateChange) {
      onUiStateChange(evt);
    }
  };
  render() {
    return /*#__PURE__*/_jsx(DateTimeRangePicker, {
      ...this.state,
      ...this.props,
      onChange: this.onChange,
      onUiStateChange: this.onUiStateChange
    });
  }
}
Uncontrolled.Controlled = DateTimeRangePicker;
Uncontrolled.Uncontrolled = Uncontrolled;
Uncontrolled.defaultRangeTypes = defaultRangeTypes;
Uncontrolled.getRange = (value, rangeTypes = defaultRangeTypes) => {
  if (typeof value === 'string') {
    const range = rangeTypes.find(r => r.value === value);
    return range && range.getRange() || {};
  }
  return value || {};
};
export default Uncontrolled;